import { useCallback } from 'react';
import {
  SearchSelectField,
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';

const PAGE_SIZE = 20;
const BASE = '/admin/v1/notification/templates';

interface TemplateView {
  templateId: string;
  templateCode: string;
  channel: string;
  languageCode: string;
}

interface ListEnvelope {
  success: boolean;
  data?: { items?: TemplateView[]; total?: number } | null;
}

interface DetailEnvelope {
  success: boolean;
  data?: TemplateView | null;
}

const toOption = (t: TemplateView): SearchSelectOption => ({
  value: t.templateId,
  label: t.templateCode,
  hint: [t.channel, t.languageCode].filter(Boolean).join(' · ') || undefined,
});

interface Props {
  channel?: string;
  /** 언어 필터(KO_KR 등). 채널×언어 매트릭스에서 해당 언어 변형만 노출. */
  languageCode?: string;
  /** 선택된 templateId. */
  value: string | null;
  onChange: (templateId: string | null) => void;
  placeholder?: string;
}

/**
 * 템플릿 선택(값=templateId). 자동 알림 규칙 templateBindings 가 templateId 를 요구하므로
 * code 기반 NotificationTemplateSearchSelect 대신 id 기반 변형. 표시는 템플릿 코드.
 * languageCode 지정 시 그 언어 변형만 필터(채널×언어 매트릭스).
 */
export const NotificationTemplateIdSelect = ({ channel, languageCode, value, onChange, placeholder }: Props) => {
  const loadOptions = useCallback(
    async (keyword: string, page: number): Promise<SearchSelectPage> => {
      const qs = new URLSearchParams({ size: String(PAGE_SIZE), page: String(page) });
      if (keyword.trim()) qs.set('q', keyword.trim());
      if (channel) qs.set('channel', channel);
      // BE 템플릿 목록은 languageCode 필터를 지원(TemplateTab 필터와 동일 param).
      if (languageCode) qs.set('languageCode', languageCode);
      const res = await adminFetch(`${BASE}?${qs.toString()}`);
      if (!res.ok) throw new Error(`템플릿 검색 실패 (${res.status})`);
      const env = (await res.json()) as ListEnvelope;
      const items = (env.data?.items ?? []).map(toOption);
      const total = env.data?.total;
      const hasMore = total != null ? (page + 1) * PAGE_SIZE < total : items.length === PAGE_SIZE;
      return total != null ? { items, hasMore, total } : { items, hasMore };
    },
    [channel, languageCode],
  );

  const resolveOption = useCallback(async (templateId: string): Promise<SearchSelectOption | null> => {
    const res = await adminFetch(`${BASE}/${templateId}`);
    if (!res.ok) return { value: templateId, label: templateId };
    const env = (await res.json()) as DetailEnvelope;
    return env.data ? toOption(env.data) : { value: templateId, label: templateId };
  }, []);

  return (
    <SearchSelectField
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      resolveOption={resolveOption}
      placeholder={placeholder ?? '템플릿 선택'}
    />
  );
};
